import { useState, useEffect } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetAuth, isApiConfigured } from "@/services/http";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";

// ── Types ─────────────────────────────────────────────────────────────────────

interface OverviewStats {
  totalBusinesses:  number;
  pendingApprovals: number;
  totalUsers:       number;
  totalLeads:       number;
  unreadLeads:      number;
  leadsThisWeek:    number;
  pendingOrders:    number;
  paidOrders:       number;
  totalRevenue:     number;
  pendingReviews:   number;
}

interface CategoryStat { categoryName: string; businessCount: number; }
interface DistrictStat  { districtName: string; businessCount: number; }
interface LeadDay       { leadDate: string; leadCount: number; }
interface PlanOrder     { planSlug: string; billingCycle: string; orderCount: number; confirmedRevenue: number; }
interface RecentOrder   { id: number; planSlug: string; contactName: string; total: number; status: string; createdAt: string; businessName: string | null; }

interface AnalyticsData {
  overview:     OverviewStats;
  byCategory:   CategoryStat[];
  byDistrict:   DistrictStat[];
  leadsByDay:   LeadDay[];
  ordersByPlan: PlanOrder[];
  recentOrders: RecentOrder[];
}

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO: AnalyticsData = {
  overview: {
    totalBusinesses: 16, pendingApprovals: 5, totalUsers: 43,
    totalLeads: 87, unreadLeads: 12, leadsThisWeek: 19,
    pendingOrders: 4, paidOrders: 11, totalRevenue: 32450, pendingReviews: 6,
  },
  byCategory: [
    { categoryName: "Restaurants & Cafes",     businessCount: 4 },
    { categoryName: "Hotels & Resorts",         businessCount: 3 },
    { categoryName: "Healthcare & Wellness",    businessCount: 3 },
    { categoryName: "Electronics & IT",         businessCount: 3 },
    { categoryName: "Education & Coaching",     businessCount: 1 },
    { categoryName: "Legal & Professional",     businessCount: 1 },
    { categoryName: "Clothing & Fashion",       businessCount: 1 },
    { categoryName: "Automobile & Vehicles",    businessCount: 1 },
    { categoryName: "Supermarkets & Groceries", businessCount: 1 },
  ],
  byDistrict: [
    { districtName: "South Canara (Dakshina Kannada)", businessCount: 8 },
    { districtName: "Udupi",                           businessCount: 4 },
    { districtName: "North Canara (Uttara Kannada)",   businessCount: 4 },
  ],
  leadsByDay: [
    { leadDate: "2026-09-01", leadCount: 3 },
    { leadDate: "2026-09-02", leadCount: 5 },
    { leadDate: "2026-09-03", leadCount: 2 },
    { leadDate: "2026-09-04", leadCount: 8 },
    { leadDate: "2026-09-05", leadCount: 4 },
    { leadDate: "2026-09-06", leadCount: 6 },
    { leadDate: "2026-09-07", leadCount: 7 },
    { leadDate: "2026-09-08", leadCount: 5 },
    { leadDate: "2026-09-09", leadCount: 9 },
    { leadDate: "2026-09-10", leadCount: 11 },
    { leadDate: "2026-09-11", leadCount: 8 },
    { leadDate: "2026-09-12", leadCount: 13 },
    { leadDate: "2026-09-13", leadCount: 7 },
    { leadDate: "2026-09-14", leadCount: 9 },
  ],
  ordersByPlan: [
    { planSlug: "premium",  billingCycle: "yearly", orderCount: 3,  confirmedRevenue: 4797  },
    { planSlug: "business", billingCycle: "yearly", orderCount: 8,  confirmedRevenue: 4792  },
    { planSlug: "free",     billingCycle: "yearly", orderCount: 6,  confirmedRevenue: 0     },
  ],
  recentOrders: [
    { id: 11, planSlug: "premium",  contactName: "Dr. Nisha D'Silva",  total: 1886.82, status: "pending",   createdAt: "2026-09-14T09:15:00Z", businessName: null },
    { id: 10, planSlug: "business", contactName: "Suresh Bhat",        total: 706.82,  status: "paid",      createdAt: "2026-09-13T15:44:00Z", businessName: "Bhat Bakery" },
    { id: 9,  planSlug: "business", contactName: "Lakshmi Hegde",      total: 706.82,  status: "paid",      createdAt: "2026-09-12T11:20:00Z", businessName: "Hegde Flowers" },
    { id: 8,  planSlug: "premium",  contactName: "Rajan Mallya",       total: 1886.82, status: "pending",   createdAt: "2026-09-11T08:50:00Z", businessName: null },
    { id: 7,  planSlug: "free",     contactName: "Savitha Nayak",      total: 0,        status: "pending",   createdAt: "2026-09-10T14:30:00Z", businessName: null },
  ],
};

// ── Palette for bar charts ────────────────────────────────────────────────────

const BAR_COLORS = [
  "oklch(0.52 0.18 250)",  // primary-500-like
  "oklch(0.58 0.16 200)",
  "oklch(0.55 0.19 160)",
  "oklch(0.60 0.16 120)",
  "oklch(0.54 0.14 85)",
  "oklch(0.57 0.13 300)",
  "oklch(0.56 0.16 340)",
  "oklch(0.52 0.17 20)",
  "oklch(0.55 0.14 60)",
  "oklch(0.50 0.18 190)",
];

const PLAN_COLORS: Record<string, string> = {
  free:     "#6b7280",
  business: "oklch(0.52 0.18 250)",
  premium:  "oklch(0.62 0.19 55)",
};

function fmt(n: number) { return n.toLocaleString("en-IN"); }
function fmtDate(d: string) {
  const dt = new Date(d);
  return `${dt.getDate()} ${dt.toLocaleString("en-IN", { month: "short" })}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  label, value, icon, color, bg, sub,
}: { label: string; value: string | number; icon: string; color: string; bg: string; sub?: string }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${bg}`}>
        <i className={`${icon} text-xl ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="font-heading text-2xl font-bold text-foreground-950 tabular-nums">{fmt(value as number)}</p>
        <p className="truncate text-xs text-foreground-500">{label}</p>
        {sub && <p className="text-[11px] text-foreground-400">{sub}</p>}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function Analytics() {
  const { accessToken } = useAuth();
  const [data,    setData]    = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!isApiConfigured() || !accessToken) {
      setTimeout(() => { setData(DEMO); setLoading(false); }, 400);
      return;
    }
    setLoading(true);
    setError("");
    apiGetAuth<{ data: AnalyticsData }>("/admin/analytics", accessToken)
      .then((res) => setData(res.data))
      .catch((err: Error) => setError(err.message ?? "Failed to load analytics."))
      .finally(() => setLoading(false));
  }, [accessToken, reloadToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <AdminDataError
        message={error || "Unable to load analytics right now."}
        onRetry={() => setReloadToken((t) => t + 1)}
      />
    );
  }

  const d = data;
  const ov = d.overview;

  return (
    <div className="space-y-8">
      {/* ── Overview stats ────────────────────────────────────────── */}
      <div>
        <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">Platform Overview</h2>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard label="Active Listings"   value={ov.totalBusinesses}  icon="ri-store-2-line"          color="text-primary-600"   bg="bg-primary-50" />
          <StatCard label="Pending Approvals" value={ov.pendingApprovals} icon="ri-time-line"             color="text-amber-600"     bg="bg-amber-50"   sub="require review" />
          <StatCard label="Registered Users"  value={ov.totalUsers}       icon="ri-user-3-line"           color="text-accent-600"    bg="bg-accent-50"  />
          <StatCard label="Total Leads"       value={ov.totalLeads}       icon="ri-mail-open-line"        color="text-secondary-600" bg="bg-secondary-50" sub={`${ov.unreadLeads} unread`} />
          <StatCard label="Confirmed Revenue" value={`₹${fmt(ov.totalRevenue)}`} icon="ri-money-rupee-circle-line" color="text-emerald-600" bg="bg-emerald-50" sub={`${ov.paidOrders} paid orders`} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="This Week Leads"  value={ov.leadsThisWeek}  icon="ri-calendar-check-line"  color="text-primary-600"   bg="bg-primary-50" />
          <StatCard label="Pending Orders"   value={ov.pendingOrders}  icon="ri-shopping-cart-line"   color="text-amber-600"     bg="bg-amber-50"   />
          <StatCard label="Pending Reviews"  value={ov.pendingReviews} icon="ri-star-half-line"        color="text-rose-600"      bg="bg-rose-50"    />
          <StatCard label="Paid Orders"      value={ov.paidOrders}     icon="ri-checkbox-circle-line" color="text-emerald-600"   bg="bg-emerald-50" />
        </div>
      </div>

      {/* ── Charts row ────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Leads per day */}
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Leads per Day (Last 14 Days)</h3>
          <p className="mb-5 text-xs text-foreground-500">Daily inbound lead volume across all form types.</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.leadsByDay} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-background-200, #e5e7eb)" />
              <XAxis
                dataKey="leadDate"
                tickFormatter={fmtDate}
                tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }}
                tickLine={false}
                axisLine={false}
                interval={1}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                labelFormatter={fmtDate}
                formatter={(v: number) => [`${v} leads`, "Leads"]}
              />
              <Line
                type="monotone"
                dataKey="leadCount"
                stroke="oklch(0.52 0.18 250)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "oklch(0.52 0.18 250)", strokeWidth: 0 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Businesses by district */}
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Listings by District</h3>
          <p className="mb-5 text-xs text-foreground-500">Active business count per coastal district.</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={d.byDistrict}
              layout="vertical"
              margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-background-200, #e5e7eb)" />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                type="category"
                dataKey="districtName"
                width={160}
                tick={{ fontSize: 10, fill: "var(--color-foreground-700, #374151)" }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: string) => v.split(" (")[0]}
              />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
                formatter={(v: number) => [`${v} listings`, "Businesses"]}
              />
              <Bar dataKey="businessCount" radius={[0, 4, 4, 0]}>
                {d.byDistrict.map((_entry, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Businesses by category */}
      <div className="rounded-xl border border-background-200 bg-background-50 p-6">
        <h3 className="mb-1 font-heading text-sm font-bold text-foreground-950">Listings by Category</h3>
        <p className="mb-5 text-xs text-foreground-500">Active businesses per directory category (top 10).</p>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={d.byCategory} margin={{ top: 4, right: 8, left: -20, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-background-200, #e5e7eb)" />
            <XAxis
              dataKey="categoryName"
              tick={{ fontSize: 10, fill: "var(--color-foreground-600, #4b5563)" }}
              tickLine={false}
              axisLine={false}
              angle={-35}
              textAnchor="end"
              interval={0}
              tickFormatter={(v: string) => v.split(" & ")[0].split(" (")[0].slice(0, 18)}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: "var(--color-foreground-500, #6b7280)" }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}
              formatter={(v: number) => [`${v} listings`, "Businesses"]}
            />
            <Bar dataKey="businessCount" radius={[4, 4, 0, 0]}>
              {d.byCategory.map((_entry, i) => (
                <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Orders by plan + recent orders table side by side */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Orders by plan */}
        <div className="rounded-xl border border-background-200 bg-background-50 p-6">
          <h3 className="mb-4 font-heading text-sm font-bold text-foreground-950">Orders by Plan</h3>
          <div className="space-y-3">
            {d.ordersByPlan.map((row, i) => {
              const maxRev = Math.max(...d.ordersByPlan.map((r) => r.confirmedRevenue), 1);
              const pct    = Math.round((row.confirmedRevenue / maxRev) * 100);
              return (
                <div key={i}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium capitalize text-foreground-700">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: PLAN_COLORS[row.planSlug] ?? "#9ca3af" }} />
                      {row.planSlug} ({row.billingCycle})
                    </span>
                    <span className="text-foreground-500">
                      {row.orderCount} orders · ₹{fmt(row.confirmedRevenue)}
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, background: PLAN_COLORS[row.planSlug] ?? "#9ca3af" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent orders table */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="border-b border-background-200 px-6 py-4">
            <h3 className="font-heading text-sm font-bold text-foreground-950">Recent Orders</h3>
          </div>
          <div className="divide-y divide-background-100">
            {d.recentOrders.map((order) => (
              <div key={order.id} className="flex items-center gap-3 px-6 py-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-background-200 bg-background-100">
                  <i className="ri-file-list-3-line text-sm text-foreground-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-foreground-900">{order.contactName}</p>
                  <p className="text-[11px] text-foreground-500">
                    {order.planSlug} plan
                    {order.businessName ? ` · ${order.businessName}` : ""}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs font-semibold text-foreground-900">
                    {order.total > 0 ? `₹${fmt(order.total)}` : "Free"}
                  </p>
                  <span className={`text-[10px] font-bold capitalize ${
                    order.status === "paid"    ? "text-emerald-600" :
                    order.status === "pending" ? "text-amber-600"   : "text-foreground-400"
                  }`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}